import { gql } from '__generated__';
import { useTranslation } from 'react-i18next';

import {
  EditFeaturesInput, Gender,
  GetFeaturesForProjectQuery as GetFeaturesForProject,
} from '__generated__/graphql';

import { GetSessionQuery } from '../Auth/SessionProvider';
import { WithRefetch } from '../../common/helpers';
import { useNotifier } from '../../common/Notifications/NotificationProvider';
import UI from '../../common/UI';
import useMutation from '../../api/useMutation';
import useQuery from '../../api/useQuery';
import useScroll from '../../common/useScroll';

gql(`
  fragment FeatureFields on Project {
    id
    genders
    enable_abandoned_cart_reminders
    enable_assignment_reminders
  }
`);

export const EditFeaturesMutation = gql(`
  mutation EditFeatures($input: EditFeaturesInput!) {
    editFeatures(input: $input) {
      ...FeatureFields
    }
  }
`);

export const GetFeaturesForProjectQuery = gql(`
  query GetFeaturesForProject($id: ID!) {
    project(id: $id) {
      ...FeatureFields
    }
  }
`);

interface EditFeaturesPageProps {
  projectId: string;
}

const EditFeaturesPage = ({ projectId, refetchQueries = [GetSessionQuery] }: EditFeaturesPageProps & WithRefetch) => {
  const { data } = useQuery(GetFeaturesForProjectQuery, {
    variables: {
      id: projectId,
    },
  });

  const project = data?.project;

  if (!project) {
    return null;
  }

  return <EditFeaturesView project={project} refetchQueries={refetchQueries} />;
};

const EditFeaturesView = ({ project, refetchQueries }: GetFeaturesForProject & WithRefetch) => {
  const { t } = useTranslation();
  const notifier = useNotifier();
  const { scrollTo } = useScroll();

  const genderNeutral = project.genders.includes(Gender.neutral);
  const abandonedCartReminders = project.enable_abandoned_cart_reminders;
  const assignmentReminders = project.enable_assignment_reminders;

  const [editFeatures, { error }] = useMutation(
    EditFeaturesMutation, {
      refetchQueries,
      onCompleted: () => {
        notifier.success(t('notifications.features_saved'));
      },
      onError: () => {
        scrollTo('ProjectTab');
      },
    },
  );

  const toggle = (option: keyof Omit<EditFeaturesInput, 'project'>, current: boolean) => {
    editFeatures({
      variables: {
        input: {
          project: {
            id: project.id,
          },
          [option]: !current,
        },
      },
    });
  };

  return (
    <UI.Container sc={{ compact: true, px: [0, 4] }}>
      <UI.GridContainer>
        <UI.Div>
          <UI.H3>
            {t('features')}
          </UI.H3>
          <UI.InputDescription sc={{ mt: 1 }}>
            {t('features_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.Form onSubmit={() => editFeatures()}>
          <UI.Card>
            <UI.FormGrid>
              <UI.ServerErrors error={error} />

              <UI.FormGrid sc={{ columns: '2fr 1fr', alignVertical: 'center' }}>
                <UI.Div sc={{ muted: !genderNeutral, pt: 0.25 }}>
                  <UI.InputLabel sc={{ mb: 0 }} htmlFor="FeatureGenderNeutral">
                    {t('gender_neutral_feature')}
                  </UI.InputLabel>
                  {t('gender_neutral_description')}
                </UI.Div>

                <UI.Div sc={{ textAlign: 'right' }}>
                  <UI.Toggle
                    id="FeatureGenderNeutral"
                    checked={genderNeutral}
                    onChange={() => toggle('gender_neutral', genderNeutral)}
                  />
                </UI.Div>
              </UI.FormGrid>

              <UI.FormGrid sc={{ columns: '2fr 1fr', alignVertical: 'center' }}>
                <UI.Div sc={{ muted: !abandonedCartReminders, pt: 0.25 }}>
                  <UI.InputLabel sc={{ mb: 0 }} htmlFor="FeatureAbandonedCartReminders">
                    {t('abandoned_cart_reminders')}
                  </UI.InputLabel>
                  {t('abandoned_cart_reminders_description')}
                </UI.Div>

                <UI.Div sc={{ textAlign: 'right' }}>
                  <UI.Toggle
                    id="FeatureAbandonedCartReminders"
                    checked={abandonedCartReminders}
                    onChange={() => toggle('enable_abandoned_cart_reminders', abandonedCartReminders)}
                  />
                </UI.Div>
              </UI.FormGrid>

              <UI.FormGrid sc={{ columns: '2fr 1fr', alignVertical: 'center' }}>
                <UI.Div sc={{ muted: !assignmentReminders, pt: 0.25 }}>
                  <UI.InputLabel sc={{ mb: 0 }} htmlFor="FeatureAssignmentReminders">
                    {t('assignment_reminders')}
                  </UI.InputLabel>
                  {t('assignment_reminders_description')}
                </UI.Div>

                <UI.Div sc={{ textAlign: 'right' }}>
                  <UI.Toggle
                    id="FeatureAssignmentReminders"
                    checked={assignmentReminders}
                    onChange={() => toggle('enable_assignment_reminders', assignmentReminders)}
                  />
                </UI.Div>
              </UI.FormGrid>
            </UI.FormGrid>
          </UI.Card>
        </UI.Form>
      </UI.GridContainer>
    </UI.Container>
  );
};

export default EditFeaturesPage;
